import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import styles from './QBRMaterialsDetail.module.css';
import bgPattern from '../../../../Assets/BRANDING-BG.png';
import { Header } from '../header/Header';
import { DashboardSidebar } from '../DashboardSidebar/DashboardSidebar';

const ACTION_ITEMS_DATA = [
    {
        id: 'ITOPS-1423',
        actionItem: 'Replace end-of-life firewall at HQ',
        owner: 'Sudesh Sonki',
        dueDate: '28 Feb 2026',
        status: 'In Progress',
        notes: 'Hardware selected, awaiting delivery'
    },
    {
        id: 'ITOPS-1424',
        actionItem: 'Complete patching remediation for remaining servers',
        owner: 'Baldev',
        dueDate: '31 Jan 2026',
        status: 'Open',
        notes: '6 servers pending maintenance window'
    },
    {
        id: 'ITOPS-1425',
        actionItem: 'Review ISP redundancy options',
        owner: 'Customer',
        dueDate: '15 Feb 2026',
        status: 'Open',
        notes: 'Cost vs risk discussion required'
    },
    {
        id: 'ITOPS-1426',
        actionItem: 'Roll out EDR to remaining 25 endpoints',
        owner: 'MSP',
        dueDate: '14 Feb 2026',
        status: 'Complete',
        notes: 'Deployment completed post-QBR'
    },
    {
        id: 'ITOPS-1427',
        actionItem: 'Confirm budget approval for Q2 initiatives',
        owner: 'Customer',
        dueDate: '31 Jan 2026',
        status: 'Open',
        notes: 'Required before next phase'
    }
];

const KEY_TAKEAWAYS = [
    'Overall service stability improved quarter-over-quarter, with reduced critical incidents and faster resolution times',
    'Security posture strengthened through improved patch compliance and endpoint coverage, though legacy firewall remains a risk',
    'Ticket volume normalized following onboarding-related spikes earlier in the year',
    'Infrastructure is currently stable but approaching capacity limits in the primary site',
    'Alignment achieved on priorities for the next quarter, with focus on security and resilience'
];

export const QBRMaterialsDetail = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [currentWeekIndex, setCurrentWeekIndex] = useState(0);
    const navigate = useNavigate();

    // Define constant dates
    const qbrDate = '7th January 2026';
    const nextQBR = '9th April 2026';

    // Define period covered options that change with arrow navigation
    const periods = [
        'Q4 2025 (1st Oct - 31st Dec)',
        'Q4 2025 (8th Oct - 7th Jan)',
        'Q4 2025 (15th Oct - 14th Jan)'
    ];

    const currentPeriod = periods[currentWeekIndex];

    const handlePreviousWeek = () => {
        if (currentWeekIndex > 0) {
            setCurrentWeekIndex(currentWeekIndex - 1);
        }
    };

    const handleNextWeek = () => {
        if (currentWeekIndex < periods.length - 1) {
            setCurrentWeekIndex(currentWeekIndex + 1);
        }
    };

    const handleBack = () => {
        navigate('/dashboard/qbr');
    };

    const getStatusClassName = (status) => {
        switch (status.toLowerCase()) {
            case 'in progress':
                return styles.statusInProgress;
            case 'open':
                return styles.statusOpen;
            case 'complete':
                return styles.statusComplete;
            default:
                return '';
        }
    };

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.headerWrapper}>
                <Header onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
            </div>

            <div className={styles.mainWrapper}>
                <DashboardSidebar
                    isOpen={isSidebarOpen}
                    onClose={() => setIsSidebarOpen(false)}
                />

                <div className={styles.container} style={{ backgroundImage: `url(${bgPattern})` }}>
                    <button className={styles.backButton} onClick={handleBack}>
                        <ChevronLeft size={16} />
                        Back to QBR & Shared Materials
                    </button>

                    <div className={styles.pageHeader}>
                        <h1 className={styles.title}>QBR Materials</h1>
                    </div>

                    <div className={styles.dateControlsContainer}>
                        <div className={styles.boxstylecont}>
                            <div className={styles.boxstyle}>
                                <div className={styles.dateControl}>
                                    <span className={styles.dateLabel}>Date of QBR</span>
                                    <span className={styles.dateValue}>{qbrDate}</span>
                                </div>
                            </div>
                            <div className={styles.boxstyle}>
                                                        <button
                                            className={styles.arrowButton}
                                            onClick={handlePreviousWeek}
                                            disabled={currentWeekIndex === 0}
                                        >
                                            <ChevronLeft size={24} />
                                        </button>

                                        <div className={styles.dateControl}>
                                            <span className={styles.dateLabel}>Period Covered</span>
                                            <span className={styles.dateValue}>{currentPeriod}</span>
                                        </div>

                                        <button
                                            className={styles.arrowButton}
                                            onClick={handleNextWeek}
                                            disabled={currentWeekIndex === periods.length - 1}
                                        >
                                            <ChevronRight size={24} />
                                    </button>

                            </div>
                            <div className={styles.boxstyle}>
                                <div className={styles.dateControl}>
                                    <span className={styles.dateLabel}>Next QBR</span>
                                    <span className={styles.dateValue}>{nextQBR}</span>
                                </div>
                            </div>
                        </div>


                        <div className={styles.gradientborder}>
                                <button className={styles.downloadButton}>
                                    <Download size={18} />
                                        QBR Slide Deck
                                </button>
                        </div>
                       

                        

                        
                    </div>

                    <div className={styles.contentSection}>
                        <div className={styles.keyTakeawaysSection}>
                            <h2 className={styles.sectionTitle}>Key Takeaways</h2>
                            <ul className={styles.takeawaysList}>
                                {KEY_TAKEAWAYS.map((takeaway, index) => (
                                    <li key={index}>{takeaway}</li>
                                ))}
                            </ul>
                        </div>

                        <div className={styles.actionItemsSection}>
                            <h2 className={styles.sectionTitle}>Action Items</h2>
                            <div className={styles.tableContainer}>
                                <table className={styles.actionItemsTable}>
                                    <thead>
                                        <tr>
                                            <th>Ticket ID</th>
                                            <th>Action Item</th>
                                            <th>Owner</th>
                                            <th>Due Date</th>
                                            <th>Status</th>
                                            <th>Notes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ACTION_ITEMS_DATA.map((item) => (
                                            <tr key={item.id}>
                                                <td>{item.id}</td>
                                                <td>{item.actionItem}</td>
                                                <td>{item.owner}</td>
                                                <td>{item.dueDate}</td>
                                                <td>
                                                    <span className={`${styles.statusBadge} ${getStatusClassName(item.status)}`}>
                                                        {item.status}
                                                    </span>
                                                </td>
                                                <td>{item.notes}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QBRMaterialsDetail;
